import { useState, useEffect, useRef, useCallback, type ReactElement } from 'react';
import { motion, useInView } from 'framer-motion';
import { useChat } from '../hooks/useChat';
import { trackDemoInteraction } from '../utils/analytics';

// ─── Terminal Configuration ───────────────────────────────
// Simulated terminal that presents Kempion's research brief.
// Typewriter effect triggers on scroll-into-view.
// Audio infrastructure ready for future ambient sounds.

type LineStyle = 'command' | 'system' | 'status' | 'text' | 'header' | 'project' | 'detail' | 'diagram' | 'highlight' | 'blank';

interface TermLine {
    text: string;
    style: LineStyle;
    charMs?: number;   // ms per character override
    pauseMs?: number;  // ms pause after line override
}

// Character typing speed by style (ms per character)
const CHAR_SPEED: Record<LineStyle, number> = {
    command: 28,
    system: 14,
    status: 10,
    text: 20,
    header: 0,
    project: 16,
    detail: 16,
    diagram: 0,
    highlight: 26,
    blank: 0,
};

// Pause after line completes (ms)
const LINE_PAUSE: Record<LineStyle, number> = {
    command: 400,
    system: 150,
    status: 80,
    text: 120,
    header: 200,
    project: 100,
    detail: 50,
    diagram: 60,
    highlight: 300,
    blank: 60,
};

// ─── Terminal Content ─────────────────────────────────────

const LINES: TermLine[] = [
    { text: '$ kempion --research-brief', style: 'command' },
    { text: '', style: 'blank' },
    { text: 'kempion-research v0.1.0', style: 'system' },
    { text: 'loading modules...', style: 'system', pauseMs: 500 },
    { text: '', style: 'blank' },
    { text: '  calibration engine  ··· ONLINE', style: 'status' },
    { text: '  verification loop   ··· ACTIVE', style: 'status' },
    { text: '  memory architecture ··· LEARNING', style: 'status' },
    { text: '  convergence monitor ··· TRACKING', style: 'status' },
    { text: '', style: 'blank' },
    { text: '── RESEARCH BRIEF ─────────────────────', style: 'header' },
    { text: '', style: 'blank' },
    { text: 'Building intelligence that improves', style: 'text' },
    { text: 'by being honest about uncertainty.', style: 'text' },
    { text: '', style: 'blank' },
    { text: '  predict ──→ verify ──→ remember ─╮', style: 'diagram' },
    { text: '    ╰──────────── improve ←────────╯', style: 'diagram' },
    { text: '', style: 'blank' },
    { text: '▸ VORTEX · calibrated forecasting', style: 'project' },
    { text: '  Three weather models compete per field.', style: 'detail' },
    { text: '  35% accuracy improvement via calibration.', style: 'detail' },
    { text: '', style: 'blank' },
    { text: '▸ PUPIL · earned confidence', style: 'project' },
    { text: '  Every prediction scored against reality.', style: 'detail' },
    { text: '  Track record, not assertion.', style: 'detail' },
    { text: '', style: 'blank' },
    { text: '▸ CORTEX · compounding memory', style: 'project' },
    { text: '  Past outcomes shape future decisions.', style: 'detail' },
    { text: '  The system remembers what works.', style: 'detail' },
    { text: '', style: 'blank' },
    { text: 'calibration + confidence + memory = convergence', style: 'text' },
    { text: '', style: 'blank' },
    { text: 'thesis: honesty compounds ◆', style: 'highlight' },
    { text: '', style: 'blank' },
];

// ─── Chat Starter Questions ─────────────────────────────
const STARTER_QUESTIONS = [
    'What does "honesty compounds" mean?',
    'How do you test these ideas in practice?',
    "What's the hardest problem you're working on?",
];

// ─── Line rendering (rich colors for completed lines) ─────

const BASE_COLORS: Record<LineStyle, string> = {
    command: 'text-cyan-400',
    system: 'text-gray-500',
    status: 'text-gray-400',
    text: 'text-gray-400',
    header: 'text-gray-600',
    project: 'text-cyan-300',
    detail: 'text-gray-500',
    diagram: 'text-gray-600',
    highlight: 'text-white',
    blank: '',
};

function renderCompletedLine(line: TermLine): ReactElement {
    const { text, style } = line;

    if (style === 'blank') return <>{'\u00A0'}</>;

    // Status: color the status word (ONLINE, ACTIVE, etc.)
    if (style === 'status') {
        const parts = text.split('···');
        if (parts.length === 2) {
            const word = parts[1].trim();
            const color = word === 'ONLINE' || word === 'ACTIVE'
                ? 'text-green-400'
                : word === 'LEARNING'
                ? 'text-amber-400'
                : 'text-cyan-400';
            return (
                <>
                    <span className="text-gray-600">{parts[0]}··· </span>
                    <span className={color}>{word}</span>
                </>
            );
        }
    }

    // Command: $ in gray, rest in cyan
    if (style === 'command') {
        if (text === '$') return <span className="text-gray-600">$</span>;
        return (
            <>
                <span className="text-gray-600">$ </span>
                <span className="text-cyan-400">{text.slice(2)}</span>
            </>
        );
    }

    // Project: ▸ NAME · description
    if (style === 'project') {
        const match = text.match(/^(▸ )(\w+)( · )(.+)$/);
        if (match) {
            return (
                <>
                    <span className="text-gray-600">{match[1]}</span>
                    <span className="text-cyan-400">{match[2]}</span>
                    <span className="text-gray-700">{match[3]}</span>
                    <span className="text-gray-300">{match[4]}</span>
                </>
            );
        }
    }

    // Highlight: thesis line
    if (style === 'highlight' && text.startsWith('thesis:')) {
        return (
            <>
                <span className="text-gray-600">thesis: </span>
                <span className="text-white">honesty compounds</span>
                <span className="text-cyan-400"> ◆</span>
            </>
        );
    }

    return <>{text}</>;
}


// ─── Component ────────────────────────────────────────────

export const ResearchTerminal = () => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const termBodyRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const isInView = useInView(sectionRef, { once: true, amount: 0.15, margin: '-150px' });

    // Line 0 ("$ kempion --research-brief") is pre-rendered as a static teaser.
    // Typing animation starts from line 1 when scrolled into view.
    const [completedLines, setCompletedLines] = useState(1);
    const [currentChars, setCurrentChars] = useState(0);
    const [showCursor, setShowCursor] = useState(true);
    const [typingDone, setTypingDone] = useState(false);
    const [chatInput, setChatInput] = useState('');

    const timerRef = useRef<number>(0);

    // Chat hook
    const { messages, isLoading, streamingContent, sendMessage, clearChat } = useChat();
    const hasMessages = messages.length > 0 || !!streamingContent;

    // Export conversation to markdown file
    const exportToMarkdown = useCallback(() => {
        if (messages.length === 0) return;
        const date = new Date().toISOString().slice(0, 10);
        const lines = [
            `# Kempion Research — Conversation`,
            `*Exported ${new Date().toLocaleString()}*`,
            '',
            '---',
            '',
        ];
        for (const msg of messages) {
            if (msg.role === 'user') {
                lines.push(`**You:** ${msg.content}`, '');
            } else {
                lines.push(msg.content, '');
            }
        }
        const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `kempion-chat-${date}.md`;
        a.click();
        URL.revokeObjectURL(url);
        trackDemoInteraction('terminal_chat', 'export_md', { message_count: messages.length });
    }, [messages]);

    // Cursor blink
    useEffect(() => {
        const id = setInterval(() => setShowCursor(v => !v), 530);
        return () => clearInterval(id);
    }, []);

    // Focus input when typing finishes
    useEffect(() => {
        if (typingDone && inputRef.current) {
            inputRef.current.focus();
        }
    }, [typingDone]);

    // Cmd+S to export conversation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 's' && messages.length > 0) {
                e.preventDefault();
                exportToMarkdown();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [messages, exportToMarkdown]);

    // Auto-scroll terminal body on new messages / streaming
    useEffect(() => {
        if (termBodyRef.current && hasMessages) {
            termBodyRef.current.scrollTop = termBodyRef.current.scrollHeight;
        }
    }, [messages, streamingContent, hasMessages]);

    // Typing engine — drives state from local closure variables
    useEffect(() => {
        if (!isInView) return;

        let lineIdx = 1; // Line 0 already shown as static teaser
        let charIdx = 0;
        let cancelled = false;

        const advance = () => {
            if (cancelled || lineIdx >= LINES.length) {
                setTypingDone(true);
                return;
            }

            const line = LINES[lineIdx];
            const speed = line.charMs ?? CHAR_SPEED[line.style];
            const pause = line.pauseMs ?? LINE_PAUSE[line.style];

            // Instant lines (blank, header, diagram, or speed=0)
            if (speed === 0 || line.text.length === 0) {
                lineIdx++;
                setCompletedLines(lineIdx);
                setCurrentChars(0);
                charIdx = 0;
                timerRef.current = window.setTimeout(advance, pause);
                return;
            }

            // Type one character
            charIdx++;
            setCurrentChars(charIdx);

            if (charIdx >= line.text.length) {
                // Line complete
                lineIdx++;
                setCompletedLines(lineIdx);
                setCurrentChars(0);
                charIdx = 0;
                timerRef.current = window.setTimeout(advance, pause);
            } else {
                timerRef.current = window.setTimeout(advance, speed);
            }
        };

        // Initial delay before typing starts
        timerRef.current = window.setTimeout(advance, 600);

        return () => {
            cancelled = true;
            clearTimeout(timerRef.current);
        };
    }, [isInView]);

    const handleSend = (text: string) => {
        if (!text.trim() || isLoading) return;
        trackDemoInteraction('terminal_chat', 'message_sent', { message_length: text.length });
        sendMessage(text);
        setChatInput('');
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleSend(chatInput);
    };

    const handleStarterClick = (q: string) => {
        trackDemoInteraction('terminal_chat', 'starter_clicked', { question: q });
        handleSend(q);
    };

    const currentLine = LINES[completedLines];
    const isTyping = currentLine && currentChars > 0;

    const cursor = (
        <span
            className={`inline-block w-[0.55em] h-[1.15em] bg-cyan-400/80 align-text-bottom ml-px ${
                showCursor ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transition: 'opacity 0.1s' }}
        />
    );

    return (
        <section ref={sectionRef} className="relative py-16 md:py-24 px-4 md:px-24">
            <div className="max-w-3xl mx-auto">
                {/* Eyebrow */}
                <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                    className="text-[10px] uppercase tracking-[0.4em] text-gray-600 font-mono mb-6"
                >
                    Live research
                </motion.p>

                {/* Terminal Window */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="rounded-xl border border-gray-800/80 overflow-hidden shadow-2xl shadow-cyan-900/10"
                >
                    {/* Title Bar */}
                    <div className="flex items-center gap-2 px-4 py-3 bg-gray-900/80 border-b border-gray-800/50">
                        <div className="flex gap-1.5">
                            <span className="w-3 h-3 rounded-full bg-red-500/60" />
                            <span className="w-3 h-3 rounded-full bg-yellow-500/60" />
                            <span className="w-3 h-3 rounded-full bg-green-500/60" />
                        </div>
                        <span className="text-[11px] text-gray-600 font-mono ml-2">
                            kempion-research — bash
                        </span>

                        {/* Chat actions — only visible when there are messages */}
                        {hasMessages && (
                            <div className="ml-auto flex items-center gap-3">
                                <button
                                    onClick={exportToMarkdown}
                                    className="text-[10px] text-gray-600 hover:text-gray-400 transition-colors font-mono"
                                    title="Export conversation as markdown"
                                >
                                    export .md
                                </button>
                                <button
                                    onClick={clearChat}
                                    className="text-[10px] text-gray-600 hover:text-gray-400 transition-colors font-mono"
                                >
                                    clear
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Terminal Body */}
                    <div
                        ref={termBodyRef}
                        className="bg-[#080808] px-5 py-5 md:px-6 md:py-6 font-mono text-[12px] md:text-[13px] leading-[1.7] min-h-[420px] max-h-[600px] overflow-y-auto overflow-x-auto"
                        role="log"
                        aria-label="Research terminal output"
                    >
                        {/* Completed lines — rich rendering */}
                        {LINES.slice(0, completedLines).map((line, i) => (
                            <div
                                key={i}
                                className={`${BASE_COLORS[line.style]} whitespace-pre`}
                            >
                                {renderCompletedLine(line)}
                            </div>
                        ))}

                        {/* Currently typing line — base color, partial text + cursor */}
                        {isTyping && (
                            <div className={`${BASE_COLORS[currentLine.style]} whitespace-pre`}>
                                {currentLine.text.slice(0, currentChars)}
                                {cursor}
                            </div>
                        )}

                        {/* ─── Post-typing: Chat Interface ─── */}
                        {typingDone && !isTyping && (
                            <>
                                {/* Starter questions — clickable terminal commands */}
                                {!hasMessages && (
                                    <div className="mt-1">
                                        <div className="text-gray-600 whitespace-pre">{'── try asking ─────────────────────────'}</div>
                                        <div className="text-gray-600 whitespace-pre">{'\u00A0'}</div>
                                        {STARTER_QUESTIONS.map((q) => (
                                            <button
                                                key={q}
                                                onClick={() => handleStarterClick(q)}
                                                className="group block w-full text-left py-1 text-gray-400 hover:text-cyan-400 transition-all duration-150 cursor-pointer"
                                            >
                                                <span className="text-gray-700 group-hover:text-cyan-600 transition-colors">{'  › '}</span>
                                                <span className="group-hover:underline underline-offset-2 decoration-cyan-800">{q}</span>
                                            </button>
                                        ))}
                                        <div className="text-gray-600 whitespace-pre">{'\u00A0'}</div>
                                    </div>
                                )}

                                {/* Chat messages — rendered inline in terminal */}
                                {messages.map((msg) => (
                                    <div key={msg.id} className="mt-1">
                                        {msg.role === 'user' ? (
                                            <div className="whitespace-pre">
                                                <span className="text-gray-600">$ </span>
                                                <span className="text-cyan-400">{msg.content}</span>
                                            </div>
                                        ) : (
                                            <div className="whitespace-pre-wrap text-gray-400 pl-2">
                                                {msg.content}
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {/* Streaming response */}
                                {streamingContent && (
                                    <div className="mt-1 whitespace-pre-wrap text-gray-400 pl-2">
                                        {streamingContent}
                                        <span className="inline-block w-[0.55em] h-[1.15em] bg-cyan-400/80 align-text-bottom ml-px animate-pulse" />
                                    </div>
                                )}

                                {/* Live input prompt */}
                                <form onSubmit={handleSubmit} className="flex items-center mt-1">
                                    <span className="text-gray-600">$ </span>
                                    {!isLoading && !chatInput && cursor}
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        value={chatInput}
                                        onChange={(e) => setChatInput(e.target.value)}
                                        placeholder={isLoading ? '' : 'ask anything...'}
                                        disabled={isLoading}
                                        className="flex-1 bg-transparent text-cyan-400 placeholder:text-gray-700 outline-none disabled:opacity-50 ml-1 caret-cyan-400"
                                    />
                                    {isLoading && (
                                        <span className="flex items-center gap-1 ml-2">
                                            <span className="w-1 h-1 bg-cyan-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                                            <span className="w-1 h-1 bg-cyan-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                                            <span className="w-1 h-1 bg-cyan-400 rounded-full animate-bounce" />
                                        </span>
                                    )}
                                </form>
                            </>
                        )}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
